/**
 * ============================================================================
 * MASTER PAGE: Dynamic Navigation Menus & Actions (`tbl_menus`, `tbl_operations` - Sheets 13 & 14)
 * ============================================================================
 * Purpose:
 *   Admin interface for managing menu hierarchies, parent-child routes,
 *   drag-and-drop reordering, and action permissions.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { menuService } from "../../services";

// SVG Icons
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const PencilSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const ChevronDownIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const ArrowUpIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
  </svg>
);

const ArrowDownIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
  </svg>
);

const Bars3BottomLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
  </svg>
);

const ListBulletIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>
);

const FolderIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
  </svg>
);

const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

export default function MenuManagement() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("tree"); // "tree" | "table"
  const [search, setSearch] = useState("");
  const [expandedParents, setExpandedParents] = useState({});
  const [saveStatus, setSaveStatus] = useState("");

  // Modal State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menuType, setMenuType] = useState("root"); // "root" | "submenu"
  const [formData, setFormData] = useState({
    str_menu_name: "",
    str_page_name: "",
    int_parent_menu_id: 0,
    int_order: 1,
    str_class_name: "FolderIcon",
    bool_is_active: true,
  });

  const loadMenus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await menuService.get({ limit: 200, sortBy: "int_order", sortOrder: "asc" });
      if (res && res.success) {
        const sorted = (res.data || []).sort(
          (a, b) => (Number(a.int_order) || 0) - (Number(b.int_order) || 0)
        );
        setMenus(sorted);
      }
    } catch (err) {
      console.error("Failed to load menus:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  const showFeedback = (text) => {
    setSaveStatus(text);
    setTimeout(() => setSaveStatus(""), 3500);
  };

  // Group into root parents & children map
  const { parentMenus, childrenMap } = useMemo(() => {
    const parents = [];
    const children = {};

    menus.forEach((item) => {
      const parentId = Number(item.int_parent_menu_id) || 0;
      if (parentId === 0) {
        parents.push(item);
      } else {
        if (!children[parentId]) children[parentId] = [];
        children[parentId].push(item);
      }
    });

    parents.sort((a, b) => (Number(a.int_order) || 0) - (Number(b.int_order) || 0));
    Object.keys(children).forEach((k) => {
      children[k].sort((a, b) => (Number(a.int_order) || 0) - (Number(b.int_order) || 0));
    });

    return { parentMenus: parents, childrenMap: children };
  }, [menus]);

  // Toggle tree expansion
  const toggleParentExpand = (parentId) => {
    setExpandedParents((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  const handleExpandAll = () => {
    const allExpanded = {};
    parentMenus.forEach((p) => {
      allExpanded[p.int_menu_id] = true;
    });
    setExpandedParents(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedParents({});
  };

  // Sync Reorder
  const syncReorder = async (reorderedList) => {
    try {
      const payload = reorderedList.map((item, idx) => ({
        int_menu_id: item.int_menu_id,
        int_order: idx + 1,
        int_parent_menu_id: item.int_parent_menu_id,
      }));

      await menuService.reorder(payload);
      showFeedback("Order updated and synchronized!");
      loadMenus();
    } catch (err) {
      console.error("Reorder failed:", err);
      alert("Failed to save reordered positions: " + err.message);
      loadMenus();
    }
  };

  const handleMoveUp = (item, list) => {
    const idx = list.findIndex((x) => x.int_menu_id === item.int_menu_id);
    if (idx <= 0) return;
    const newList = [...list];
    const [moved] = newList.splice(idx, 1);
    newList.splice(idx - 1, 0, moved);
    syncReorder(newList);
  };

  const handleMoveDown = (item, list) => {
    const idx = list.findIndex((x) => x.int_menu_id === item.int_menu_id);
    if (idx < 0 || idx >= list.length - 1) return;
    const newList = [...list];
    const [moved] = newList.splice(idx, 1);
    newList.splice(idx + 1, 0, moved);
    syncReorder(newList);
  };

  const handleOpenCreate = (preselectedParent = null) => {
    setSelectedMenu(null);
    if (preselectedParent) {
      setMenuType("submenu");
      const existingChildren = childrenMap[preselectedParent.int_menu_id] || [];
      setFormData({
        str_menu_name: "",
        str_page_name: "",
        int_parent_menu_id: preselectedParent.int_menu_id,
        int_order: existingChildren.length + 1,
        str_class_name: "DocumentTextIcon",
        bool_is_active: true,
      });
    } else {
      setMenuType("root");
      setFormData({
        str_menu_name: "",
        str_page_name: "",
        int_parent_menu_id: 0,
        int_order: parentMenus.length + 1,
        str_class_name: "FolderIcon",
        bool_is_active: true,
      });
    }
    setIsFormOpen(true);
  };

  const handleOpenEdit = (menu) => {
    setSelectedMenu(menu);
    const isRoot = menu.int_parent_menu_id === 0;
    setMenuType(isRoot ? "root" : "submenu");
    setFormData({
      str_menu_name: menu.str_menu_name || "",
      str_page_name: menu.str_page_name || "",
      int_parent_menu_id: menu.int_parent_menu_id || 0,
      int_order: menu.int_order || 1,
      str_class_name: menu.str_class_name || "FolderIcon",
      bool_is_active: menu.bool_is_active ?? true,
    });
    setIsFormOpen(true);
  };

  const handleSaveMenu = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        int_parent_menu_id: menuType === "root" ? 0 : Number(formData.int_parent_menu_id),
        int_order: Number(formData.int_order) || 1,
      };

      if (selectedMenu) {
        await menuService.edit(selectedMenu._id || selectedMenu.int_menu_id, payload);
        showFeedback("Menu updated successfully!");
      } else {
        await menuService.create(payload);
        showFeedback("New menu created successfully!");
      }

      setIsFormOpen(false);
      loadMenus();
    } catch (err) {
      alert("Failed to save menu: " + err.message);
    }
  };

  const handleDeleteMenu = async (menu) => {
    const hasChildren = (childrenMap[menu.int_menu_id] || []).length > 0;
    const confirmMsg = hasChildren
      ? `Menu "${menu.str_menu_name}" has ${childrenMap[menu.int_menu_id].length} submenus. Deleting it will affect its hierarchy. Proceed?`
      : `Are you sure you want to delete "${menu.str_menu_name}"?`;

    if (window.confirm(confirmMsg)) {
      try {
        await menuService.delete(menu._id || menu.int_menu_id);
        showFeedback("Menu removed.");
        loadMenus();
      } catch (err) {
        alert("Failed to delete menu: " + err.message);
      }
    }
  };

  const filteredParents = useMemo(() => {
    if (!search.trim()) return parentMenus;
    const q = search.toLowerCase();
    return parentMenus.filter((p) => {
      const matchParent =
        p.str_menu_name?.toLowerCase().includes(q) ||
        p.str_page_name?.toLowerCase().includes(q);
      const matchChild = (childrenMap[p.int_menu_id] || []).some(
        (c) =>
          c.str_menu_name?.toLowerCase().includes(q) ||
          c.str_page_name?.toLowerCase().includes(q)
      );
      return matchParent || matchChild;
    });
  }, [parentMenus, childrenMap, search]);

  const totalMenus = menus.length;
  const rootCount = parentMenus.length;
  const subMenuCount = Object.values(childrenMap).flat().length;
  const activeCount = menus.filter((m) => m.bool_is_active !== false).length;

  return (
    <div className="space-y-6 mx-auto pb-12">
      {/* Toast Feedback */}
      {saveStatus && (
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-xs font-semibold flex items-center gap-2 shadow-sm animate-fade-in">
          <CheckCircleIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <span>{saveStatus}</span>
        </div>
      )}

      {/* Header section with Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
              Dynamic Navigation Menus
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              Hierarchy tree, parent-child governance, and drag-and-drop display ordering for sidebar navigation
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={loadMenus}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => handleOpenCreate(null)}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-sm shadow-green-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Root Menu
            </button>
          </div>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white font-mono">
            {totalMenus}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Total Navigation Links
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-blue-600 font-mono">
            {rootCount}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Root Categories
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-purple-600 font-mono">
            {subMenuCount}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Submenu Pages
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-green-600 font-mono">
            {activeCount}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Active Routes
          </div>
        </div>
      </div>

      {/* Controls Bar: Search & View Switcher */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center justify-between">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search menu name or URL path..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          <div className="flex items-center gap-2">
            {viewMode === "tree" && (
              <>
                <button
                  type="button"
                  onClick={handleExpandAll}
                  className="px-3 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  Expand All
                </button>
                <button
                  type="button"
                  onClick={handleCollapseAll}
                  className="px-3 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  Collapse All
                </button>
              </>
            )}

            <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-700 p-1">
              <button
                type="button"
                onClick={() => setViewMode("tree")}
                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  viewMode === "tree"
                    ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900"
                }`}
              >
                <Bars3BottomLeftIcon className="w-4 h-4 mr-1.5" />
                Tree
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  viewMode === "table"
                    ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900"
                }`}
              >
                <ListBulletIcon className="w-4 h-4 mr-1.5" />
                Table
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content: Tree View */}
      {viewMode === "tree" && (
        <div className="space-y-4">
          {filteredParents.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-200 dark:border-gray-700">
              <FolderIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-bold text-gray-900 dark:text-white">No Menus Found</h3>
              <p className="text-xs text-gray-500 mt-1 mb-4">No menu categories match your criteria.</p>
              <button
                onClick={() => handleOpenCreate(null)}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl text-xs font-semibold cursor-pointer"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add Root Menu
              </button>
            </div>
          ) : (
            filteredParents.map((parent, pIdx) => {
              const children = childrenMap[parent.int_menu_id] || [];
              const isExpanded = expandedParents[parent.int_menu_id] !== false;

              return (
                <div
                  key={parent._id || parent.int_menu_id}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* Root Category Header Row */}
                  <div className="p-4 sm:p-5 flex items-center justify-between gap-3 bg-gray-50/70 dark:bg-gray-700/40 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => toggleParentExpand(parent.int_menu_id)}
                        className="p-1 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="w-5 h-5" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5" />
                        )}
                      </button>
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-purple-600 to-blue-600 text-white flex items-center justify-center font-bold shadow-sm">
                        <FolderIcon className="w-4 h-4" />
                      </div>
                      <div className="truncate">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-bold text-gray-900 dark:text-white text-sm truncate">
                            {parent.str_menu_name}
                          </h3>
                          <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold">
                            Position #{parent.int_order}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 font-mono truncate">
                          {parent.str_page_name || "Parent Category"} • {children.length} submenus
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleMoveUp(parent, parentMenus)}
                        disabled={pIdx === 0}
                        className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 cursor-pointer"
                        title="Move Up"
                      >
                        <ArrowUpIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(parent, parentMenus)}
                        disabled={pIdx === parentMenus.length - 1}
                        className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 cursor-pointer"
                        title="Move Down"
                      >
                        <ArrowDownIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenCreate(parent)}
                        className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 text-xs font-semibold hover:bg-green-100 transition-colors cursor-pointer"
                      >
                        <PlusIcon className="w-3.5 h-3.5 mr-1" />
                        Submenu
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenEdit(parent)}
                        className="p-1.5 text-gray-500 hover:text-emerald-600 rounded-lg cursor-pointer"
                        title="Edit Menu"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteMenu(parent)}
                        className="p-1.5 text-gray-500 hover:text-rose-600 rounded-lg cursor-pointer"
                        title="Delete Menu"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Submenus Drawer */}
                  {isExpanded && children.length > 0 && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700/60 bg-white dark:bg-gray-800">
                      {children.map((sub, sIdx) => (
                        <div
                          key={sub._id || sub.int_menu_id}
                          className="px-6 py-3.5 flex items-center justify-between gap-3 hover:bg-gray-50/80 dark:hover:bg-gray-750 transition-colors"
                        >
                          <div className="flex items-center space-x-3 pl-6">
                            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center font-bold">
                              <DocumentTextIcon className="w-3.5 h-3.5" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white text-xs">
                                {sub.str_menu_name}
                              </div>
                              <div className="text-[11px] font-mono text-gray-400">
                                {sub.str_page_name}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-1.5">
                            <span className="font-mono text-[10px] text-gray-400 mr-2">
                              #{sub.int_order}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleMoveUp(sub, children)}
                              disabled={sIdx === 0}
                              className="p-1 rounded-md border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-gray-700 disabled:opacity-30 cursor-pointer"
                            >
                              <ArrowUpIcon className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveDown(sub, children)}
                              disabled={sIdx === children.length - 1}
                              className="p-1 rounded-md border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-gray-700 disabled:opacity-30 cursor-pointer"
                            >
                              <ArrowDownIcon className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(sub)}
                              className="p-1 text-gray-400 hover:text-emerald-600 rounded-md cursor-pointer"
                            >
                              <PencilSquareIcon className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteMenu(sub)}
                              className="p-1 text-gray-400 hover:text-rose-600 rounded-md cursor-pointer"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Main Content: Flat Table View */}
      {viewMode === "table" && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/60">
                <tr>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-14">#</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Menu Item</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hierarchy Level</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {menus.map((row, index) => {
                  const isRoot = row.int_parent_menu_id === 0;
                  const parent = parentMenus.find((p) => p.int_menu_id === row.int_parent_menu_id);

                  return (
                    <tr key={row._id || index} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400 font-mono">
                        #{row.int_order || index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-white text-xs ${
                            isRoot ? "bg-purple-600" : "bg-blue-600"
                          }`}>
                            <FolderIcon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white text-xs">
                              {row.str_menu_name}
                            </div>
                            <div className="text-[11px] font-mono text-gray-400">
                              {row.str_page_name || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isRoot ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                            Root Parent Category
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">
                            ↳ Submenu of: <strong className="text-gray-900 dark:text-white">{parent ? parent.str_menu_name : `#${row.int_parent_menu_id}`}</strong>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          row.bool_is_active !== false
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        }`}>
                          {row.bool_is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleOpenEdit(row)}
                            className="p-1.5 text-gray-500 hover:text-emerald-600 rounded-lg cursor-pointer"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteMenu(row)}
                            className="p-1.5 text-gray-500 hover:text-rose-600 rounded-lg cursor-pointer"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Menu Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-700 transition-all">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedMenu ? "Edit Navigation Menu" : menuType === "root" ? "Add Root Parent Menu" : "Add Submenu"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure menu route slug and hierarchy position
                </p>
              </div>
              <button
                onClick={() => setIsFormOpen(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveMenu} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                  Menu Title *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Campaign Reports"
                  value={formData.str_menu_name}
                  onChange={(e) => setFormData({ ...formData, str_menu_name: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                  Page Route / Slug (URL)
                </label>
                <input
                  type="text"
                  placeholder="e.g. /user/campaigns/reports"
                  value={formData.str_page_name}
                  onChange={(e) => setFormData({ ...formData, str_page_name: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-green-500"
                />
              </div>

              {menuType === "submenu" && (
                <div>
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                    Parent Category *
                  </label>
                  <select
                    required
                    value={formData.int_parent_menu_id}
                    onChange={(e) => setFormData({ ...formData, int_parent_menu_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  >
                    {parentMenus.map((p) => (
                      <option key={p.int_menu_id} value={p.int_menu_id}>
                        {p.str_menu_name} (#{p.int_menu_id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1">
                  Order Sequence Number
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={formData.int_order}
                  onChange={(e) => setFormData({ ...formData, int_order: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-mono"
                />
              </div>

              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="bool_menu_active"
                  checked={formData.bool_is_active}
                  onChange={(e) => setFormData({ ...formData, bool_is_active: e.target.checked })}
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
                <label htmlFor="bool_menu_active" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Visible in Navigation Bar
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 mt-6">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white shadow-sm shadow-green-500/20 transition-all cursor-pointer"
                >
                  {selectedMenu ? "Update Menu" : "Save Menu"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
